import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Provider } from 'react-redux';
// Custom CSS files - imported after Bootstrap to ensure higher priority
import './App.css';
import './style.css';
import './index.css';

// Redux Store
import store from './store/store';

// Context
import { AppProvider } from './contexts/AppContext';

// Components
import Loader from './components/Loader';
import Toaster from './components/Toaster';

// Wireframe-based pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Companies from './pages/Companies';
import Settings from './pages/Settings';
import Plans from './pages/Plans';
import Payments from './pages/Payments';
import LocationCheck from './components/LocationCheck';
import ForgotPassword from './pages/ForgotPassword';
import { CookieNames, hasCookie, deleteAllCookies, getCookie } from './helpers/cookie.helper';
import ResetPassword from './pages/ResetPassword';
import { useUser } from './store/hooks/useUser';
import { logout } from './services/login.service';
import SessionManager from './components/SessionManager';
import { Constants } from './constants';

const ProtectedRoute = ({ children }) => {
  const { clearUser } = useUser();
  const navigate = useNavigate();
  const isAuthenticated = hasCookie(CookieNames.AUTH_TOKEN);
  if (!isAuthenticated) {
    clearUser();
    deleteAllCookies();
    navigate('/login', { replace: true });
    return null;
  }
  return children;
};

// Layout component that wraps all routes with LocationCheck
const AppLayout = () => {
  const { fetchUserProfile, updateUser, user } = useUser();
  const isAuthenticated = hasCookie(CookieNames.AUTH_TOKEN);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Loan Mantra - Super Admin';
    const interval = setInterval(() => {
      const currentTime = new Date().getTime();
      const lastLoginTime = new Date(getCookie(CookieNames.LAST_LOGIN_TIME)).getTime();
      const elapsedTime = currentTime - lastLoginTime;
      const timeToLogout = Math.max(0, Constants.SESSION_TIMEOUT - elapsedTime);
      if (timeToLogout >= 0) {
        updateUser({ timeToLogout: Math.ceil((timeToLogout / 1000) / 60) }); // Convert seconds to minutes
      }
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserProfile();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && user.timeToLogout?.toString()?.length && user.timeToLogout <= 5) {
      if (['SHOW_WARNING', 'CANCEL'].includes(user.refreshTokenWarningAction) && user.timeToLogout <= 0) {
        logout(getCookie(CookieNames.EMAIL)).then(() => {
          updateUser({ refreshTokenWarningAction: 'CANCEL' });
          navigate('/login', { replace: true });
        });
      } else {
        updateUser({ refreshTokenWarningAction: 'SHOW_WARNING' });
      }
    }
  }, [isAuthenticated, user.timeToLogout]);

  return (
    <LocationCheck>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />}
        />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/companies" element={<ProtectedRoute><Companies /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/plans" element={<ProtectedRoute><Plans /></ProtectedRoute>} />
        <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
        <Route
          path="/forgot-password"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPassword />}
        />
        <Route path="/ResetPasswordLink" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ResetPassword />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </LocationCheck>
  );
};

function App() {
  return (
    <Provider store={store}>
      <AppProvider>
        <Router>
          <Loader />
          <Toaster />
          <AppLayout />
          <SessionManager />
        </Router>
      </AppProvider>
    </Provider>
  );
}

export default App;

